import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output } from "@angular/core";
import { RpaDescription, TimeRange } from "@dtm-frontend/shared/ui";
import { ArrayUtils, FunctionUtils, LocalComponentStore } from "@dtm-frontend/shared/utils";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import turfCircle from "@turf/circle";
import { Polygon } from "@turf/helpers";
import { BillboardGraphics, CustomDataSource, PositionProperty } from "cesium";
import { combineLatest, filter, first, map, switchMap } from "rxjs";
import { CesiumMapService, CoordinateConverter, DtmCesiumEvent, EventResult, MapUtils, PickOptions } from "../../";
import { AirspaceElement, AirspaceElementsInfo, DronePointDetails, ZoneInfo } from "../../../geo-zones/models/geo-zones.models";
import { MapActionType, MapEntitiesEditorService } from "../../services/entity-editors/map-entities-editor.service";
import { DisposableAndPausableObservable } from "../../services/pointer-manager/cesium-events.model";
import { CesiumPointerManagerService } from "../../services/pointer-manager/cesium-pointer-manager.service";

interface GeographicalZonesInfoComponentState {
    zonesWithInfo: AirspaceElement[];
    dronePointsDetails: DronePointDetails[];
    isInfoShown: boolean;
    selectedZoneId: string | undefined;
    isEnabled: boolean;
    isProcessing: boolean;
    aupEndTime: Date | undefined;
    timeRange: TimeRange | undefined;
    airacEndTime: Date | undefined;
    isError: boolean;
    rpaDescriptions: RpaDescription[];
    isRpaDescriptionProcessing: boolean;
    isDronePointsDetailsProcessing: boolean;
}

const RPA_DESCRIPTIONS_AREA_BUFFER_RADIUS_IN_METERS = 25;

@UntilDestroy()
@Component({
    selector: "dtm-map-geographical-zones-info",
    templateUrl: "./geographical-zones-info.component.html",
    styleUrls: ["./geographical-zones-info.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LocalComponentStore],
})
export class GeographicalZonesInfoComponent implements AfterViewInit, OnDestroy {
    @Output() public readonly zoneSelectionChange = new EventEmitter<AirspaceElement | undefined>();
    @Output() public readonly getZonesInfo = new EventEmitter<ZoneInfo[]>();
    @Output() public readonly rpaDescriptionsAreaChange = new EventEmitter<Polygon>();

    @Input() public set selectedZoneId(value: string | undefined) {
        this.localStore.patchState({ selectedZoneId: value });
    }
    @Input() public set isEnabled(value: BooleanInput) {
        this.localStore.patchState({ isEnabled: coerceBooleanProperty(value) });
        if (!coerceBooleanProperty(value)) {
            this.localStore.patchState({ zonesWithInfo: [], dronePointsDetails: [], isInfoShown: false });
            this.removePinFromMap();
        }
    }
    @Input() public set zonesWithInfo(value: AirspaceElementsInfo | undefined) {
        this.localStore.patchState({
            zonesWithInfo: value?.airspaceElements ?? [],
            isProcessing: false,
            aupEndTime: value?.aupEndTime,
            timeRange: value?.scopeTimeRange,
            airacEndTime: value?.airacEndTime,
        });
    }
    @Input() public set dronePointsDetails(value: DronePointDetails[] | undefined) {
        this.localStore.patchState({ dronePointsDetails: value ?? [] });
    }
    @Input() public set isDronePointsDetailsProcessing(value: BooleanInput) {
        this.localStore.patchState({ isDronePointsDetailsProcessing: coerceBooleanProperty(value) });
    }
    @Input() public set isError(value: BooleanInput) {
        this.localStore.patchState({ isError: coerceBooleanProperty(value) });
    }
    @Input() public set isRpaDescriptionProcessing(value: BooleanInput) {
        this.localStore.patchState({ isRpaDescriptionProcessing: coerceBooleanProperty(value) });
    }
    @Input() public set rpaDescriptions(value: RpaDescription[] | undefined) {
        this.localStore.patchState({ rpaDescriptions: value ?? [] });
    }

    protected readonly selectedZoneId$ = this.localStore.selectByKey("selectedZoneId");
    protected readonly isInfoShown$ = this.localStore.selectByKey("isInfoShown");
    protected readonly isEnabled$ = this.localStore.selectByKey("isEnabled");
    protected readonly zonesWithInfo$ = this.localStore.selectByKey("zonesWithInfo");
    protected readonly dronePointsDetails$ = this.localStore.selectByKey("dronePointsDetails");
    protected readonly isProcessing$ = combineLatest([
        this.localStore.selectByKey("isProcessing"),
        this.localStore.selectByKey("isRpaDescriptionProcessing"),
        this.localStore.selectByKey("isDronePointsDetailsProcessing"),
    ]).pipe(
        map(
            ([isProcessing, isRpaDescriptionProcessing, isDronePointsDetailsProcessing]) =>
                isProcessing || isRpaDescriptionProcessing || isDronePointsDetailsProcessing
        )
    );
    protected readonly aupEndTime$ = this.localStore.selectByKey("aupEndTime");
    protected readonly timeRange$ = this.localStore.selectByKey("timeRange");
    protected readonly airacEndTime$ = this.localStore.selectByKey("airacEndTime");
    protected readonly isError$ = this.localStore.selectByKey("isError");
    protected readonly rpaDescriptions$ = this.localStore.selectByKey("rpaDescriptions");

    protected readonly pinDataSource = new CustomDataSource();

    constructor(
        private readonly eventManager: CesiumPointerManagerService,
        private readonly coordinateConverter: CoordinateConverter,
        private readonly cesiumService: CesiumMapService,
        private readonly localStore: LocalComponentStore<GeographicalZonesInfoComponentState>,
        private readonly mapEntitiesEditorService: MapEntitiesEditorService
    ) {
        this.localStore.setState({
            isInfoShown: false,
            selectedZoneId: undefined,
            isEnabled: false,
            zonesWithInfo: [],
            dronePointsDetails: [],
            isProcessing: true,
            aupEndTime: undefined,
            timeRange: undefined,
            airacEndTime: undefined,
            isError: false,
            rpaDescriptions: [],
            isRpaDescriptionProcessing: false,
            isDronePointsDetailsProcessing: false,
        });

        this.cesiumService?.getViewer().dataSources.add(this.pinDataSource);
    }

    public ngOnDestroy(): void {
        this.cesiumService?.getViewer().dataSources.remove(this.pinDataSource, true);
    }

    public ngAfterViewInit(): void {
        let eventRegistration: DisposableAndPausableObservable<EventResult> | undefined;
        this.localStore
            .selectByKey("isEnabled")
            .pipe(untilDestroyed(this))
            .subscribe((isEnabled) => {
                if (isEnabled) {
                    eventRegistration = this.watchMapClickEventAndUpdatesZonesInfo();
                } else {
                    eventRegistration?.dispose();
                }
            });
    }

    protected closeInfo() {
        this.localStore.patchState({ zonesWithInfo: [], dronePointsDetails: [], isInfoShown: false });
        this.removePinFromMap();
        this.zoneSelectionChange.emit(undefined);
    }

    protected getRpaDescription(rpaDescriptions: RpaDescription[], zone: AirspaceElement): RpaDescription | undefined {
        return rpaDescriptions.find((rpaDescription) => zone.designator === rpaDescription.designator);
    }

    private watchMapClickEventAndUpdatesZonesInfo() {
        const mapClickRegistration = this.eventManager.addEventHandler({
            event: DtmCesiumEvent.LEFT_CLICK,
            pick: PickOptions.PICK_ALL,
        });
        mapClickRegistration
            .pipe(
                switchMap((event) =>
                    this.mapEntitiesEditorService.activeMapAction$.pipe(
                        first(),
                        filter((action) => action === MapActionType.None),
                        map(() => event)
                    )
                ),
                untilDestroyed(this)
            )
            .subscribe((event) => {
                if (!this.localStore.selectSnapshotByKey("isEnabled")) {
                    return;
                }
                const zones = this.getZonesInfoFromEntities(event.cesiumEntities);

                const isInfoShown = zones.length > 0;
                this.localStore.patchState({
                    isInfoShown,
                    isProcessing: zones.some((zone) => zone.type === "designator"),
                });

                if (isInfoShown) {
                    this.localStore.patchState({ zonesWithInfo: [], dronePointsDetails: [] });
                    this.getZonesInfo.emit(zones);
                    this.getRpaDescriptionsForPosition(event.movement.endPosition);
                    this.zoneSelectionChange.emit(undefined);
                    this.setPinOnMap(event);
                } else {
                    this.removePinFromMap();
                    this.localStore.patchState({ zonesWithInfo: [], dronePointsDetails: [] });
                }
            });

        return mapClickRegistration;
    }

    private setPinOnMap(event: EventResult) {
        const pinEntity = this.pinDataSource.entities.getOrCreateEntity("pin");
        pinEntity.position = this.coordinateConverter.screenToCartesian3(event.movement.endPosition) as unknown as PositionProperty;
        pinEntity.billboard ??= {
            image: "assets/images/map-pin.png",
            scale: 0.3,
            show: true,
            verticalOrigin: 1,
        } as unknown as BillboardGraphics;
    }

    private removePinFromMap() {
        this.pinDataSource.entities.removeById("pin");
    }

    private getRpaDescriptionsForPosition(position: { x: number; y: number }) {
        const positionAsCartesian3 = this.coordinateConverter.screenToCartesian3(position);

        if (!positionAsCartesian3) {
            return;
        }

        const { longitude, latitude } = MapUtils.convertCartesian3ToSerializableCartographic(positionAsCartesian3);
        const bufferArea = turfCircle([longitude, latitude], RPA_DESCRIPTIONS_AREA_BUFFER_RADIUS_IN_METERS, { units: "meters" });

        this.rpaDescriptionsAreaChange.emit(bufferArea.geometry);
    }

    private getZonesInfoFromEntities(entities: EventResult["cesiumEntities"]): ZoneInfo[] {
        const zonesInfo =
            entities
                ?.map((entity) => {
                    if (entity.designator) {
                        return {
                            type: "designator",
                            id: entity.designator,
                        };
                    }

                    if (entity.simpleZone) {
                        return {
                            type: entity.simpleZone.type,
                            id: entity.simpleZone.id,
                        };
                    }

                    return undefined;
                })
                .filter(FunctionUtils.isTruthy) ?? [];

        return ArrayUtils.unique(zonesInfo, (item: ZoneInfo) => `${item.type}:${item.id}`);
    }
}
